/**
 * Compact job description for parsing API (avoids 504 on large payloads).
 * Uses fuzzy logic to keep relevant sections and keywords instead of blind truncation.
 *
 * Contract: Call this on the frontend (web app or extension) before sending
 * to the parse API. Scraping/extraction and compaction both happen client-side
 * so the backend receives a smaller, relevant payload.
 */

const DEFAULT_MAX_CHARS = 25000; // Keep under backend async limit so POST /async returns quickly (avoids 504 on long jobs)
const MAX_SECTION_CHARS = 5500;
const MAX_BULLET_ITEMS = 35;
const MAX_LINE_LENGTH_BEFORE_TRIM = 500;

/** Section headers (fuzzy): order and preferred labels for output */
const SECTION_PATTERNS: { pattern: RegExp; label: string }[] = [
  { pattern: /\b(about\s+the\s+role|the\s+role|role\s+overview)\b/i, label: 'About the role' },
  { pattern: /\b(responsibilities|what\s+you['']ll\s+do|key\s+responsibilities|duties)\b/i, label: 'Responsibilities' },
  { pattern: /\b(requirements?|qualifications?|what\s+we['']re\s+looking\s+for|you\s+have)\b/i, label: 'Requirements' },
  { pattern: /\b(skills?|technical\s+skills?|technologies?)\b/i, label: 'Skills' },
  { pattern: /\b(experience|years?\s+of\s+experience)\b/i, label: 'Experience' },
  { pattern: /\b(education|degree|requirements?)\b/i, label: 'Education' },
  { pattern: /\b(benefits?|perks?|we\s+offer)\b/i, label: 'Benefits' },
  { pattern: /\b(about\s+us|about\s+the\s+company|company\s+overview|who\s+we\s+are)\b/i, label: 'About the company' },
  { pattern: /\b(location|work\s+arrangement|remote|hybrid)\b/i, label: 'Location' },
  { pattern: /\b(salary|compensation|pay\s+range)\b/i, label: 'Compensation' },
  { pattern: /\b(how\s+to\s+apply|apply|application\s+process)\b/i, label: 'How to apply' },
];

/** Lines that are noise and can be dropped when they stand alone or repeat */
const NOISE_LINE_PATTERNS: RegExp[] = [
  /^\s*(apply\s+now|save\s+job|share\s+job|quick\s+apply|easy\s+apply)\s*$/i,
  /^\s*(show\s+more|show\s+less|see\s+more|see\s+less)\s*$/i,
  /^\s*(similar\s+jobs?|recommended\s+jobs?|people\s+also\s+viewed)\s*$/i,
  /^\s*(cookie\s+policy|privacy\s+policy|terms\s+of\s+service)\s*$/i,
  /^\s*[\d.]+\s*(days?|weeks?|months?)\s+ago\s*$/i,
  /^\s*©\s*\d{4}\s+/,
  /^\s*https?:\/\/\S+\s*$/,
  /^\s*[\s\-*·]{3,}\s*$/,
];

function isNoiseLine(line: string): boolean {
  const t = line.trim();
  if (t.length < 2) return true;
  for (const re of NOISE_LINE_PATTERNS) {
    if (re.test(t)) return true;
  }
  return false;
}

function compactBulletList(text: string, maxItems: number): string {
  const lines = text.split(/\n/);
  const out: string[] = [];
  let bulletCount = 0;
  let omitted = 0;
  const bulletRe = /^\s*[\-\*•·]\s+/;
  const numberedRe = /^\s*\d+[.)]\s+/;
  for (const line of lines) {
    const isBullet = bulletRe.test(line) || numberedRe.test(line);
    if (isBullet) {
      bulletCount += 1;
      if (bulletCount > maxItems) {
        omitted += 1;
        continue;
      }
    } else {
      if (omitted > 0) {
        out.push(`[… ${omitted} more list items omitted]`);
        omitted = 0;
      }
      bulletCount = 0;
    }
    out.push(line);
  }
  if (omitted > 0) {
    out.push(`[… ${omitted} more list items omitted]`);
  }
  return out.join('\n');
}

function capSection(section: string, maxChars: number): string {
  if (section.length <= maxChars) return section;
  const trimmed = section.slice(0, maxChars);
  const lastNewline = trimmed.lastIndexOf('\n');
  if (lastNewline > maxChars * 0.7) {
    return trimmed.slice(0, lastNewline + 1) + '\n[Section truncated for length]';
  }
  return trimmed + '\n[Section truncated for length]';
}

function dropRepeatedLines(text: string): string {
  const lines = text.split(/\n/);
  const seen = new Map<string, number>();
  const out: string[] = [];
  const repeatThreshold = 3;
  for (const line of lines) {
    const key = line.trim().toLowerCase().slice(0, 100);
    const count = (seen.get(key) ?? 0) + 1;
    seen.set(key, count);
    if (count <= repeatThreshold) {
      out.push(line);
    }
  }
  return out.join('\n');
}

function trimVeryLongLines(text: string, maxLen: number): string {
  return text
    .split('\n')
    .map((line) => (line.length > maxLen ? line.slice(0, maxLen) + '…' : line))
    .join('\n');
}

export interface CompactJobDescriptionOptions {
  maxChars?: number;
  maxSectionChars?: number;
  maxBulletItems?: number;
}

/**
 * Compacts a job description for the parse API using fuzzy section detection
 * and relevance preservation. Prefer this over blind truncation.
 */
export function compactJobDescriptionForParsing(
  raw: string,
  options: CompactJobDescriptionOptions = {}
): string {
  const maxChars = options.maxChars ?? DEFAULT_MAX_CHARS;
  const maxSectionChars = options.maxSectionChars ?? MAX_SECTION_CHARS;
  const maxBulletItems = options.maxBulletItems ?? MAX_BULLET_ITEMS;

  if (!raw || typeof raw !== 'string') return '';
  let text = raw
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (text.length <= maxChars) {
    return trimVeryLongLines(text, MAX_LINE_LENGTH_BEFORE_TRIM);
  }

  // Find all section header positions (fuzzy)
  const hits: { index: number; label: string }[] = [];
  for (const { pattern, label } of SECTION_PATTERNS) {
    let m: RegExpExecArray | null;
    const re = new RegExp(pattern.source, 'gi');
    while ((m = re.exec(text)) !== null) {
      hits.push({ index: m.index, label });
    }
  }
  hits.sort((a, b) => a.index - b.index);
  // Dedupe overlapping (same index or within a few chars)
  const unique: { index: number; label: string }[] = [];
  for (const h of hits) {
    if (unique.length === 0 || h.index >= unique[unique.length - 1].index + 5) {
      unique.push(h);
    }
  }

  const sections: { label: string; start: number; end: number }[] = [];
  if (unique.length === 0) {
    sections.push({ label: 'Content', start: 0, end: text.length });
  } else {
    if (unique[0].index > 0) {
      sections.push({ label: 'Summary', start: 0, end: unique[0].index });
    }
    for (let i = 0; i < unique.length; i++) {
      const start = unique[i].index;
      const end = i + 1 < unique.length ? unique[i + 1].index : text.length;
      sections.push({ label: unique[i].label, start, end });
    }
  }

  const parts: string[] = [];
  let total = 0;

  for (const { label, start, end } of sections) {
    if (total >= maxChars) break;
    let block = text.slice(start, end).trim();
    block = block
      .split('\n')
      .filter((l) => !isNoiseLine(l))
      .join('\n');
    block = dropRepeatedLines(block);
    block = compactBulletList(block, maxBulletItems);
    block = capSection(block, maxSectionChars);
    block = trimVeryLongLines(block, MAX_LINE_LENGTH_BEFORE_TRIM);
    const header = `## ${label}\n`;
    const allowed = maxChars - total - header.length;
    if (allowed <= 0) break;
    if (block.length > allowed) {
      block = block.slice(0, allowed) + '\n[Truncated]';
    }
    parts.push(header + block);
    total += header.length + block.length;
  }

  let result = parts.join('\n\n').trim();
  if (result.length > maxChars) {
    result = result.slice(0, maxChars) + '\n[Content truncated for API limit]';
  }
  return result;
}
